import React, { useEffect, useMemo, useState } from 'react';
import { RiSendPlaneFill } from "react-icons/ri";
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import axios from 'axios';
import { scheduleMessage, getScheduled, subscribe, cancelScheduled } from '../../utils/messageScheduler';
import { useSelector } from 'react-redux';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;

const InboxMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [fullConversationHistory, setFullConversationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showBatchMessageModal, setShowBatchMessageModal] = useState(false);
  const [batchMessage, setBatchMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  // const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);
  const [showBatchEmojiPicker, setShowBatchEmojiPicker] = useState(false);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);
  const [batchSelectedImages, setBatchSelectedImages] = useState([]);
  const [batchImagePreviewUrls, setBatchImagePreviewUrls] = useState([]);
  const [isCompressingBatchImages, setIsCompressingBatchImages] = useState(false);

  // Scheduling state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledItems, setScheduledItems] = useState([]);

  const resolveRecipientName = (id) => {
    const u = uniqueUsers.find(u => u.id === id);
    return u?.name || '不明';
  };

  // Prefill from Redux cache for faster initial paint
  const cachedAdminPages = useSelector(state => state.messages?.pagesByKey || {});
  const cachedAdminMessages = useMemo(() => {
    try {
      const all = Object.values(cachedAdminPages)
        .map(p => p?.messages || [])
        .flat();
      // Admin inbox: messages sent TO admin
      return all.filter(m => m?.to_userID?.role === 'admin');
    } catch (_) { return []; }
  }, [cachedAdminPages]);

  useEffect(() => {
    // Fetch current user ID
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUserId(response.data._id);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();

    // If we have cached messages, show them immediately
    if (cachedAdminMessages.length > 0) {
      setMessages(cachedAdminMessages);
      setLoading(false);
    }
    // Always fetch fresh in background to ensure correctness
    fetchInboxMessages();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe((items) => {
      setScheduledItems(items.filter((it) => it.source === 'admin:inbox'));
    });
    setScheduledItems(getScheduled().filter((it) => it.source === 'admin:inbox'));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Extract unique users from messages
    const users = [...new Set(messages.map(msg => msg.from_userID?._id))].filter(Boolean);
    const userDetails = users.map(userId => {
      const message = messages.find(msg => msg.from_userID?._id === userId);
      return {
        id: userId,
        name: message?.from_userID?.name || message?.from_userID?.userID || '不明',
        email: message?.from_userID?.email || '',
        userID: message?.from_userID?.userID || '',
        photoLink: message?.from_userID?.photoLink
      };
    });
    setUniqueUsers(userDetails);
    
    // Filter messages based on selected user
    if (selectedUser === '') {
      setFilteredMessages(messages);
    } else {
      const filtered = messages.filter(msg => msg.from_userID?._id === selectedUser);
      setFilteredMessages(filtered);
    }
  }, [messages, selectedUser]);

  const fetchInboxMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (err) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update the local state to reflect the change
      setMessages(messages.map(msg => 
        msg._id === messageId ? { ...msg, is_read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleViewDetail = (msg) => {
    setSelectedMessage(msg);
    setShowDetailModal(true);
  };

  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '☀️', '🌙', '⭐', '🌟', '✨', '💫', '🌈', '☁️', '🌤️', '🌅'
  ];

  const handleReply = async (msg) => {
    setSelectedMessage(msg);
    setShowMessageModal(true);
    setMessage('');
    
    // Fetch conversation history
    if (msg.from_userID?._id) {
      setLoadingHistory(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/messages/conversation/${msg.from_userID._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConversationHistory(response.data);
      } catch (error) {
        console.error('Error fetching conversation history:', error);
        setConversationHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    }
  };

  const openFullHistoryInNewTab = () => {
    if (!selectedMessage?.from_userID) return;
    
    // Use pre-fetched full history (200 messages)
    const history = fullConversationHistory.length > 0 ? fullConversationHistory : conversationHistory;
    const doc = window.open('', '_blank');
    if (!doc) return;
    
    const title = `${selectedMessage.from_userID.name || '不明'}とのやり取り履歴`;
    const html = `<!doctype html>
<html lang="ja">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif; margin: 20px; padding-bottom: 80px; background: #f5f5f5; }
    h2 { margin: 0 0 16px; color: #333; }
    .msg { border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; margin: 8px 0; background: white; cursor: pointer; transition: box-shadow 0.2s; }
    .msg:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .meta { color: #666; font-size: 12px; margin-bottom: 6px; }
    .from { font-weight: bold; color: #34495e; }
    .body { word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .from-me { background-color: #e3f2fd; border-left: 3px solid #2196f3; }
    .from-other { background-color: #f5f5f5; border-left: 3px solid #4caf50; }
    .back-button { position: fixed; bottom: 20px; right: 20px; background-color: #2196f3; color: white; border: none; padding: 12px 24px; border-radius: 5px; cursor: pointer; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
    .back-button:hover { background-color: #1976d2; }
    .modal { display: none; position: fixed; z-index: 1000; left: 0; top: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5); }
    .modal-content { background-color: white; margin: 5% auto; padding: 20px; border-radius: 10px; width: 80%; max-width: 600px; max-height: 70vh; overflow-y: auto; }
    .close-modal { color: #999; float: right; font-size: 28px; font-weight: bold; cursor: pointer; }
    .close-modal:hover { color: #333; }
    .modal-body { white-space: pre-wrap; word-wrap: break-word; line-height: 1.6; margin-top: 10px; }
  </style>
  <script>
    function openModal(content, sender, date) {
      const modal = document.getElementById('messageModal');
      document.getElementById('modalSender').textContent = sender;
      document.getElementById('modalDate').textContent = date;
      document.getElementById('modalBody').textContent = content;
      modal.style.display = 'block';
    }
    function closeModal() {
      document.getElementById('messageModal').style.display = 'none';
    }
    window.onclick = function(event) {
      const modal = document.getElementById('messageModal');
      if (event.target == modal) {
        closeModal();
      }
    }
  </script>
</head>
<body>
  <h2>${title} (直近200件)</h2>
  ${history.map(h => {
    const isFromMe = h.from_userID?._id === currentUserId;
    const escapedBody = (h.body || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g,'&gt;').replace(/'/g, '&apos;').replace(/"/g, '&quot;');
    const sender = (h.from_userID?.name || '不明').replace(/'/g, '&apos;');
    const date = new Date(h.created_at).toLocaleString('ja-JP');
    return `
    <div class="msg ${isFromMe ? 'from-me' : 'from-other'}" onclick="openModal('${escapedBody}', '${sender}', '${date}')">
      <div class="meta">
        <span class="from">${h.from_userID?.name || '不明'}</span>
        ／ ${date}
      </div>
      <div class="body">${escapedBody}</div>
    </div>
  `;
  }).join('')}
  <div id="messageModal" class="modal">
    <div class="modal-content">
      <span class="close-modal" onclick="closeModal()">&times;</span>
      <h3 id="modalSender"></h3>
      <p style="color: #666; font-size: 12px;" id="modalDate"></p>
      <div class="modal-body" id="modalBody"></div>
    </div>
  </div>
  <button class="back-button" onclick="window.close()">戻る</button>
</body>
</html>`;
    doc.document.open();
    doc.document.write(html);
    doc.document.close();
  };

  const addEmoji = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
      // NOTE: Do not inject placeholder text into textarea; images are shown via previews/thumbnails.
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleBatchImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (batchSelectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingBatchImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setBatchSelectedImages(prev => [...prev, ...compressedImages]);
      setBatchImagePreviewUrls(prev => [...prev, ...compressedImages]);
      // NOTE: Do not inject placeholder text into textarea; images are shown via previews/thumbnails.
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingBatchImages(false);
    }
  };

  const removeBatchImage = (index) => {
    setBatchSelectedImages(prev => prev.filter((_, i) => i !== index));
    setBatchImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleBatchMessage = () => {
    setShowBatchMessageModal(true);
    setBatchMessage('');
    setSelectedUsers([]);
    setShowBatchEmojiPicker(false);
  };

  const addEmojiToBatch = (emoji) => {
    setBatchMessage(prev => prev + emoji);
    setShowBatchEmojiPicker(false);
  };


  // const addEmoji = (emoji) => {
  //   setBatchMessage(prev => prev + emoji);
  //   setShowEmojiPicker(false);
  // };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const selectAllUsers = () => {
    setSelectedUsers(uniqueUsers.map(user => user.id));
  };

  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  const sendBatchMessage = async () => {
    if (!batchMessage.trim() && batchSelectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    if (selectedUsers.length === 0) {
      alert('送信先のユーザーを選択してください');
      return;
    }

    try {
      const isFuture = enableSchedule && scheduledAt && new Date(scheduledAt) > new Date();
      if (isFuture) {
        scheduleMessage({
          recipients: selectedUsers,
          content: batchMessage,
          images: batchSelectedImages,
          scheduledAt,
          source: 'admin:inbox',
          type: 'batch',
        });
        alert(`予約しました (${selectedUsers.length}人)`);
        setShowBatchMessageModal(false);
        setBatchMessage('');
        setBatchSelectedImages([]);
        setBatchImagePreviewUrls([]);
        setSelectedUsers([]);
        setEnableSchedule(false);
        setScheduledAt('');
        return;
      }

      setSendingBatch(true);
      const token = localStorage.getItem('token');
      const promises = selectedUsers.map(userId => 
        axios.post(`${API_URL}/api/messages/recipientId`, {
          userId: userId,
          content: batchMessage,
          images: batchSelectedImages
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(promises);
      alert(`${selectedUsers.length}人のユーザーにメッセージを送信しました`);
      setShowBatchMessageModal(false);
      setBatchMessage('');
      setBatchSelectedImages([]);
      setBatchImagePreviewUrls([]);
      setSelectedUsers([]);
      fetchInboxMessages();
    } catch (error) {
      console.error('Error sending batch message:', error);
      alert('バッチメッセージの送信に失敗しました');
    } finally {
      setSendingBatch(false);
    }
  };

  const sendReply = async () => {
    if (!message.trim() && selectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    try {
      const isFuture = enableSchedule && scheduledAt && new Date(scheduledAt) > new Date();
      if (isFuture) {
        scheduleMessage({
          recipients: [selectedMessage.from_userID._id],
          content: message,
          images: selectedImages,
          scheduledAt,
          source: 'admin:inbox',
          type: 'reply',
          relatedMessageId: selectedMessage._id,
          markAsReplied: true,
          markAsRead: true,
        });
        alert('返信を予約しました');
        setShowMessageModal(false);
        setMessage('');
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setSelectedMessage(null);
        setConversationHistory([]);
        setEnableSchedule(false);
        setScheduledAt('');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/messages/recipientId`, {
        userId: selectedMessage.from_userID._id,
        content: message,
        images: selectedImages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.put(`${API_URL}/api/messages/${selectedMessage._id}/reply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInboxMessages();
      setShowMessageModal(false);
      setMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setMessage('');
      setSelectedMessage(null);
      setConversationHistory([]);
      alert('返信を送信しました');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert(error.response?.data?.message || '返信の送信に失敗しました');
    }
  };

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>受信メッセージ</h2>
      
      {/* User Search Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '14px',
              minWidth: '300px',
              backgroundColor: 'white'
            }}
          >
            <option value="">すべてのユーザー</option>
            {uniqueUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {/* {user.name} {user.email ? `(${user.email})` : ''} */}
                {user.name}
              </option>
            ))}
          </select>
          <div style={{ color: '#666', fontSize: '14px' }}>
            {selectedUser === '' ? 
              `全${messages.length}件` : 
              `選択されたユーザー: ${filteredMessages.length}件`
            }
          </div>
          {/* <button
            onClick={handleBatchMessage}
            style={{
              padding: '12px 20px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'background-color 0.3s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
          >
            一括メッセージ送信
          </button> */}
        </div>
      </div>
      
      {/* Scheduled items panel */}
      {/* <div style={{
        marginBottom: '20px',
        background: '#f8f9fa',
        border: '1px solid #eee',
        borderRadius: '8px',
        padding: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>⏳ 予約中のメッセージ</div>
        {scheduledItems.length === 0 ? (
          <div style={{ color: '#7f8c8d', fontSize: '12px' }}>現在、予約はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scheduledItems.map((it) => (
              <div key={it.id} style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '6px 8px',
                background: it.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(it.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{it.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>宛先: {resolveRecipientName(Array.isArray(it.recipients) ? it.recipients[0] : it.recipients)}</span>
                  {it.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {it.lastError}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => { if (window.confirm('この予約を取り消しますか？')) { cancelScheduled(it.id); } }}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div> */}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>読み込み中...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left', width: '20%' }}>送信者</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '15%' }}>受信日時</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '45%' }}>内容</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '10%' }}>状態</th>
                <th style={{ padding: '15px', textAlign: 'left', width: '10%' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredMessages.map((msg) => (
                <tr key={msg._id} style={{ borderBottom: '1px solid #ecf0f1', background: (msg.is_read || msg.has_reply) ? 'white' : '#fffbe6' }}>
                  <td style={{ 
                    padding: '15px', 
                    verticalAlign: 'top',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {msg.from_userID?.photoLink ? (
                        <img 
                          src={msg.from_userID.photoLink.photodatas || msg.from_userID.photoLink} 
                          alt="Avatar" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%',
                            objectFit: 'cover',
                            flexShrink: 0
                          }} 
                        />
                      ) : (
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          flexShrink: 0
                        }}>
                          なし
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                          {msg.from_userID?.name || msg.from_userID?.userID || '不明'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                          ID: {msg.from_userID?.userID}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ 
                    padding: '15px', 
                    fontSize: '12px', 
                    color: '#7f8c8d',
                    verticalAlign: 'top',
                    whiteSpace: 'nowrap'
                  }}>
                    {new Date(msg.created_at).toLocaleString('ja-JP')}
                  </td>
                  <td 
                    style={{ 
                      padding: '15px',
                      verticalAlign: 'top',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '0',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleViewDetail(msg)}
                  >
                    <div style={{
                      lineHeight: '1.5',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '50px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      {msg.body}
                      {msg.body && msg.body.length > 100 && (
                        <div style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          background: 'linear-gradient(transparent, white)',
                          height: '50px',
                          width: '100%',
                          pointerEvents: 'none'
                        }}></div>
                      )}
                    </div>
                    {msg.images && msg.images.length > 0 && (
                      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '5px', maxWidth: '100%', overflow: 'hidden' }}>
                        <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                      </div>
                    )}
                    {/* {msg.body && msg.body.length > 100 && (
                      <div style={{
                        fontSize: '12px',
                        color: '#3498db',
                        marginTop: '5px',
                        fontWeight: 'bold'
                      }}>
                        クリックして詳細を表示
                      </div>
                    )} */}
                  </td>
                  <td style={{ 
                    padding: '15px',
                    verticalAlign: 'top',
                    textAlign: 'center'
                  }}>
                    {(msg.is_read || msg.has_reply) ? (
                      <span style={{ color: '#27ae60', fontWeight: 'bold' }}>既読</span>
                    ) : (
                      <span style={{ color: '#e67e22', fontWeight: 'bold' }}>未読</span>
                    )}
                  </td>
                  <td style={{ 
                    padding: '15px',
                    verticalAlign: 'top',
                    textAlign: 'center'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                      <button
                        onClick={() => handleReply(msg)}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="返信"
                      >
                        <RiSendPlaneFill />
                        返信
                      </button>
                                            {!msg.is_read && !msg.has_reply && (
                      <button
                        onClick={() => markAsRead(msg._id)}
                        style={{
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                            padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                        title="既読にする"
                      >
                        ✓ 既読にする
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reply Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              {selectedMessage?.from_userID?.name}さんに返信
            </h3>
            
            {/* Conversation History */}
            {conversationHistory.length > 0 && (
              <div style={{
                marginBottom: '5px',
                maxHeight: '100px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '5px',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#666' }}>
                    直前のやり取り
                  </div>
                  <button
                    onClick={openFullHistoryInNewTab}
                    style={{
                      backgroundColor: '#2196f3',
                      color: 'white',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >
                    📋
                  </button>
                </div>
                {conversationHistory.slice(-1).map((msg) => {
                  // Check if message is from current user (admin) by comparing IDs
                  const isFromMe = msg.from_userID?._id === currentUserId;
                  return (
                    <div
                      key={msg._id}
                      style={{
                        marginBottom: '10px',
                        padding: '8px',
                        backgroundColor: isFromMe ? '#e3f2fd' : '#f5f5f5',
                        borderRadius: '5px',
                        borderLeft: `3px solid ${isFromMe ? '#2196f3' : '#4caf50'}`
                      }}
                    >
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        {msg.from_userID?.name || '不明'} - {new Date(msg.created_at).toLocaleString('ja-JP')}
                      </div>
                      <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {(msg.body || '').replace(/\[画像添付\]\n?/g, '').trim()}
                      </div>
                      {msg.images && msg.images.length > 0 && (
                        <div style={{ marginTop: '8px', maxWidth: '100%', overflow: 'hidden' }}>
                          <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {loadingHistory && (
              <div style={{ marginBottom: '20px', textAlign: 'center', color: '#666' }}>
                会話履歴を読み込み中...
              </div>
            )}
            
            <div style={{ marginBottom: '3px' }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="返信メッセージを入力してください..."
                style={{
                  width: '95%',
                  height: '100px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  resize: 'vertical',
                  marginBottom: '10px'
                }}
              />
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 1001,
                maxWidth: '400px',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '10px' }}>
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '5px',
                        borderRadius: '5px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowEmojiPicker(false)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Scheduling controls */}
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={enableSchedule}
                  onChange={(e) => setEnableSchedule(e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                後で送信を予約する
              </label>
            </div>
            {enableSchedule && (
              <div style={{ marginBottom: '10px' }}>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessage('');
                  setSelectedImages([]);
                  setImagePreviewUrls([]);
                  setConversationHistory([]);
                  setFullConversationHistory([]);
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendReply}
                disabled={!message.trim() && selectedImages.length === 0}
                style={{
                  backgroundColor: (message.trim() || selectedImages.length > 0) ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (message.trim() || selectedImages.length > 0) ? 'pointer' : 'not-allowed'
                }}
              >
                {enableSchedule && scheduledAt && new Date(scheduledAt) > new Date() ? '予約' : '送信'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Message Modal */}
      {showBatchMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>一括メッセージ送信</h3>
            
            {/* User Selection */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#34495e' }}>送信先ユーザー選択</h4>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={selectAllUsers}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    全選択
                  </button>
                  <button
                    onClick={deselectAllUsers}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    全解除
                  </button>
                </div>
              </div>
              
              <div style={{ 
                maxHeight: '200px', 
                overflow: 'auto', 
                border: '1px solid #ddd', 
                borderRadius: '5px',
                padding: '10px'
              }}>
                {uniqueUsers.map((user) => (
                  <div key={user.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px',
                    borderBottom: '1px solid #eee'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                      {user.photoLink ? (
                        <img 
                          src={user.photoLink.photodatas || user.photoLink} 
                          alt="Avatar" 
                          style={{ 
                            width: '30px', 
                            height: '30px', 
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }} 
                        />
                      ) : (
                        <div style={{ 
                          width: '30px', 
                          height: '30px', 
                          borderRadius: '50%', 
                          backgroundColor: '#ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px'
                        }}>
                          なし
                        </div>
                      )}
                      <div>
                        <span style={{ fontWeight: 'bold' }}>{user.name}</span>
                        <span style={{ color: '#666', marginLeft: '10px' }}>(ID: {user.userID || user.id})</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#34495e' }}>
                メッセージ内容
              </label>
              <div style={{ position: 'relative' }}>
                <textarea
                  value={batchMessage}
                  onChange={(e) => setBatchMessage(e.target.value)}
                  placeholder="メッセージを入力してください..."
                  style={{
                    width: '95%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                <button
                  onClick={() => setShowBatchEmojiPicker(!showBatchEmojiPicker)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                >
                  😊
                </button>
              </div>

              {/* Image Upload Section */}
              <div style={{ marginBottom: '20px', marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
                  画像を添付（最大10枚、動画ファイルは除く）
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {/* File selection button */}
                  <label
                    style={{
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: (isCompressingBatchImages || batchSelectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'inline-block',
                      opacity: (isCompressingBatchImages || batchSelectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    📷 画像を選択
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleBatchImageSelect}
                      disabled={isCompressingBatchImages || batchSelectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {/* Camera button for mobile */}
                  <label
                    style={{
                      backgroundColor: '#27ae60',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: (isCompressingBatchImages || batchSelectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'inline-block',
                      opacity: (isCompressingBatchImages || batchSelectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    📸 カメラで撮影
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleBatchImageSelect}
                      disabled={isCompressingBatchImages || batchSelectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                {isCompressingBatchImages && (
                  <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                    画像を圧縮中...
                  </div>
                )}
                {batchImagePreviewUrls.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '10px',
                    marginTop: '10px'
                  }}>
                    {batchImagePreviewUrls.map((url, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                            border: '1px solid #ddd'
                          }}
                        />
                        <button
                          onClick={() => removeBatchImage(index)}
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Scheduling controls */}
              <div style={{ marginTop: '10px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={enableSchedule}
                    onChange={(e) => setEnableSchedule(e.target.checked)}
                    style={{ marginRight: '6px' }}
                  />
                  後で送信を予約する
                </label>
              </div>
              {enableSchedule && (
                <div style={{ marginTop: '10px' }}>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBatchMessageModal(false);
                  setBatchMessage('');
                  setBatchSelectedImages([]);
                  setBatchImagePreviewUrls([]);
                  setSelectedUsers([]);
                }}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendBatchMessage}
                disabled={sendingBatch || selectedUsers.length === 0 || (!batchMessage.trim() && batchSelectedImages.length === 0)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: (sendingBatch || selectedUsers.length === 0 || (!batchMessage.trim() && batchSelectedImages.length === 0)) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '14px',
                  cursor: (sendingBatch || selectedUsers.length === 0 || (!batchMessage.trim() && batchSelectedImages.length === 0)) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {sendingBatch ? '送信中...' : (enableSchedule && scheduledAt && new Date(scheduledAt) > new Date() ? `予約 (${selectedUsers.length}人)` : `送信 (${selectedUsers.length}人)`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed View Modal */}
      {showDetailModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '600px',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>受信メッセージ詳細</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Sender Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              flexShrink: 0
            }}>
              {selectedMessage.from_userID?.photoLink ? (
                <img
                  src={selectedMessage.from_userID.photoLink.photodatas || selectedMessage.from_userID.photoLink}
                  alt="Profile"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  なし
                </div>
              )}
              <div>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginBottom: '5px'
                }}>
                  送信者: {selectedMessage.from_userID?.name || 'Unknown'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px',
                  marginBottom: '5px'
                }}>
                  ID: {selectedMessage.from_userID?.userID || 'N/A'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px'
                }}>
                  受信日時: {new Date(selectedMessage.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
              <div style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: (selectedMessage.is_read || selectedMessage.has_reply) ? '#27ae60' : '#e67e22'
              }}>
                <span>{(selectedMessage.is_read || selectedMessage.has_reply) ? '既読' : '未読'}</span>
              </div>
            </div>
            
            {/* Message Content */}
            <div style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333', flexShrink: 0 }}>メッセージ内容</h4>
              <div style={{
                lineHeight: '1.6',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
                color: '#333',
                overflow: 'auto',
                flex: 1,
                marginBottom: selectedMessage.images && selectedMessage.images.length > 0 ? '15px' : '0'
              }}>
                {selectedMessage.body}
              </div>
              {selectedMessage.images && selectedMessage.images.length > 0 && (
                <div style={{ flexShrink: 0 }}>
                  <MessageImages images={selectedMessage.images} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxMessages; 